import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { Grid3x3, AlertOctagon, ChevronDown, X } from "lucide-react";

import {
  useAllFeatureProducts,
  useUpsertFeatureProduct,
  useRemoveFeatureProduct,
} from "@/application/use-cases/products/use-feature-products.queries";
import { useProducts } from "@/application/use-cases/products/use-products.queries";
import { useFeaturesList } from "@/application/use-cases/features/use-features.queries";
import { MatrixCell } from "@/presentation/components/coverage-matrix/matrix-cell";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import { DOMAIN_COLOR, DOMAIN_LABEL, DOMAINS, type Domain } from "@/presentation/data/domains";
import type {
  FeatureProductCoverage,
  FeatureRow,
  ProductRow,
} from "@/infrastructure/supabase/database.types";

const searchSchema = z.object({
  product: z.string().optional(),
});

export const Route = createFileRoute("/_app/coverage-matrix")({
  validateSearch: searchSchema,
  component: CoverageMatrixPage,
});

function CoverageMatrixPage() {
  const productsQ = useProducts();
  const featuresQ = useFeaturesList();
  const fpQ = useAllFeatureProducts();
  const upsert = useUpsertFeatureProduct();
  const remove = useRemoveFeatureProduct();

  const products = productsQ.data ?? [];
  const features = featuresQ.data ?? [];
  const assignments = fpQ.data ?? [];

  const [domainFilter, setDomainFilter] = useState<Domain | null>(null);
  const [journeyFilter, setJourneyFilter] = useState<"all" | "B2C" | "B2B" | "both">("all");
  const [search, setSearch] = useState("");
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());

  const visibleProducts = useMemo(() => {
    if (selectedProductIds.size === 0) return products;
    return products.filter((p) => selectedProductIds.has(p.id));
  }, [products, selectedProductIds]);

  const coverageMap = useMemo(() => {
    const m = new Map<string, FeatureProductCoverage>();
    for (const a of assignments) m.set(`${a.product_id}:${a.feature_id}`, a.coverage);
    return m;
  }, [assignments]);

  const filteredFeatures = useMemo(() => {
    const q = search.toLowerCase();
    return features.filter((f) => {
      if (domainFilter && f.domain !== domainFilter) return false;
      if (journeyFilter !== "all" && f.journey !== journeyFilter) return false;
      if (q.length > 0 && !f.title.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [features, domainFilter, journeyFilter, search]);

  const { gapFeatures, sortedFeatures } = useMemo(() => {
    const gap: FeatureRow[] = [];
    const rest: FeatureRow[] = [];
    for (const f of filteredFeatures) {
      // A feature is a "platform gap" only if no product covers it with
      // native/plugin/custom. `irrelevant` rows are explicit no-coverage
      // statements per product and don't make the feature non-gap.
      const hasAny = products.some((p) => {
        const c = coverageMap.get(`${p.id}:${f.id}`);
        return c !== undefined && c !== "irrelevant";
      });
      if (!hasAny && products.length > 0) gap.push(f);
      else rest.push(f);
    }
    rest.sort((a, b) => {
      const da = DOMAINS.indexOf(a.domain as Domain);
      const db = DOMAINS.indexOf(b.domain as Domain);
      if (da !== db) return da - db;
      return a.title.localeCompare(b.title);
    });
    return { gapFeatures: gap, sortedFeatures: rest };
  }, [filteredFeatures, products, coverageMap]);

  const stats = useMemo(() => {
    const totalCells = features.length * products.length;
    const native = assignments.filter((a) => a.coverage === "native").length;
    const plugin = assignments.filter((a) => a.coverage === "plugin").length;
    const custom = assignments.filter((a) => a.coverage === "custom").length;
    const assigned = assignments.length;
    return { totalCells, native, plugin, custom, assigned };
  }, [features, products, assignments]);

  const handleCellPick = (
    product: ProductRow,
    feature: FeatureRow,
    next: FeatureProductCoverage | null
  ) => {
    if (next === null) {
      remove.mutate({ productId: product.id, featureId: feature.id });
    } else {
      upsert.mutate({
        product_id: product.id,
        feature_id: feature.id,
        coverage: next,
        notes_md: null,
        effort_estimate_weeks: null,
      });
    }
  };

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-[100rem] flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <RouteHero
        eyebrow="Coverage matrix"
        icon={Grid3x3}
        numeral={String(stats.assigned).padStart(3, "0")}
        numeralTone="cyan"
        numeralSuffix={`/ ${stats.totalCells} cells`}
        title="Which products cover which features."
        summary="Click any cell to classify. Coverage feeds the auto-derived coverage criteria on the product page. Empty rows (no product covers the feature) surface as platform gaps."
        meta={[
          { label: "Native", value: stats.native, tone: "green" },
          { label: "Plugin", value: stats.plugin, tone: "cyan" },
          { label: "Custom", value: stats.custom, tone: "amber" },
          {
            label: "Gaps",
            value: gapFeatures.length,
            tone: gapFeatures.length > 0 ? "red" : "default",
          },
        ]}
      />

      <Toolbar
        domainFilter={domainFilter}
        onDomainFilter={setDomainFilter}
        journeyFilter={journeyFilter}
        onJourneyFilter={setJourneyFilter}
        search={search}
        onSearch={setSearch}
        products={products}
        selectedProductIds={selectedProductIds}
        onSelectedProductIds={setSelectedProductIds}
      />

      {products.length === 0 ? (
        <div className="glow-card p-8 text-center">
          <p className="text-foreground/70 text-sm">No products yet.</p>
          <p className="text-muted-foreground mt-1 text-[12px]">
            Add a product on the Products page first, then return here to classify coverage.
          </p>
        </div>
      ) : (
        <>
          {gapFeatures.length > 0 ? (
            <GapsSection
              gapFeatures={gapFeatures}
              products={visibleProducts}
              onPick={handleCellPick}
            />
          ) : null}

          <MatrixTable
            features={sortedFeatures}
            products={visibleProducts}
            coverageMap={coverageMap}
            onPick={handleCellPick}
          />
        </>
      )}
    </div>
  );
}

function Toolbar({
  domainFilter,
  onDomainFilter,
  journeyFilter,
  onJourneyFilter,
  search,
  onSearch,
  products,
  selectedProductIds,
  onSelectedProductIds,
}: {
  domainFilter: Domain | null;
  onDomainFilter: (d: Domain | null) => void;
  journeyFilter: "all" | "B2C" | "B2B" | "both";
  onJourneyFilter: (j: "all" | "B2C" | "B2B" | "both") => void;
  search: string;
  onSearch: (s: string) => void;
  products: ProductRow[];
  selectedProductIds: Set<string>;
  onSelectedProductIds: (ids: Set<string>) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 print:hidden">
      <input
        type="search"
        placeholder="Filter features…"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        className="border-input h-9 w-56 rounded-md border bg-transparent px-3 text-[13px]"
      />
      <ProductFilter
        products={products}
        selectedIds={selectedProductIds}
        onChange={onSelectedProductIds}
      />
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => onDomainFilter(null)}
          className="rounded-full border px-3 py-1 text-[11px] font-medium transition-colors"
          style={
            domainFilter === null
              ? {
                  borderColor: "oklch(0.55 0.17 190)",
                  background: "oklch(0.95 0.05 190)",
                  color: "oklch(0.35 0.17 190)",
                }
              : { borderColor: "oklch(0.91 0.008 240)", color: "oklch(0.45 0.01 240)" }
          }
        >
          All domains
        </button>
        {DOMAINS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => onDomainFilter(d)}
            className="rounded-full border px-3 py-1 text-[11px] font-medium transition-colors"
            style={
              domainFilter === d
                ? {
                    borderColor: DOMAIN_COLOR[d],
                    background: `${DOMAIN_COLOR[d]}1a`,
                    color: DOMAIN_COLOR[d],
                  }
                : { borderColor: "oklch(0.91 0.008 240)", color: "oklch(0.45 0.01 240)" }
            }
          >
            {DOMAIN_LABEL[d]}
          </button>
        ))}
      </div>
      <div className="flex gap-1">
        {(["all", "B2C", "B2B", "both"] as const).map((j) => (
          <button
            key={j}
            type="button"
            onClick={() => onJourneyFilter(j)}
            className="rounded-full border px-3 py-1 text-[11px] font-medium transition-colors"
            style={
              journeyFilter === j
                ? {
                    borderColor: "oklch(0.55 0.17 190)",
                    background: "oklch(0.95 0.05 190)",
                    color: "oklch(0.35 0.17 190)",
                  }
                : { borderColor: "oklch(0.91 0.008 240)", color: "oklch(0.45 0.01 240)" }
            }
          >
            {j === "all" ? "All journeys" : j}
          </button>
        ))}
      </div>
    </div>
  );
}

function GapsSection({
  gapFeatures,
  products,
  onPick,
}: {
  gapFeatures: FeatureRow[];
  products: ProductRow[];
  onPick: (p: ProductRow, f: FeatureRow, next: FeatureProductCoverage | null) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="glow-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hover:bg-muted/30 flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition-colors print:hidden"
      >
        <div className="flex items-center gap-3">
          <AlertOctagon className="size-4" style={{ color: "oklch(0.55 0.20 25)" }} />
          <span className="text-[13px] font-semibold" style={{ color: "oklch(0.45 0.18 25)" }}>
            Platform gaps · {gapFeatures.length} feature{gapFeatures.length === 1 ? "" : "s"} no
            product covers
          </span>
        </div>
        <span className="text-foreground/50 text-[11px]">{open ? "Hide" : "Show"}</span>
      </button>
      {open ? (
        <MatrixTable
          features={gapFeatures}
          products={products}
          coverageMap={new Map()}
          onPick={onPick}
        />
      ) : null}
    </div>
  );
}

function MatrixTable({
  features,
  products,
  coverageMap,
  onPick,
}: {
  features: FeatureRow[];
  products: ProductRow[];
  coverageMap: Map<string, FeatureProductCoverage>;
  onPick: (p: ProductRow, f: FeatureRow, next: FeatureProductCoverage | null) => void;
}) {
  if (features.length === 0) {
    return (
      <div className="glow-card text-muted-foreground p-8 text-center text-sm">
        No features match the current filters.
      </div>
    );
  }
  return (
    <div className="glow-card overflow-auto" style={{ maxHeight: "75vh" }}>
      <table className="w-full border-separate" style={{ borderSpacing: 0 }}>
        <thead>
          <tr>
            <th
              className="text-foreground/70 sticky top-0 left-0 z-30 bg-white px-3 py-2 text-left text-[10.5px] font-medium tracking-[0.12em] uppercase"
              style={{ borderBottom: "1px solid oklch(0.91 0.008 240)" }}
            >
              Feature
            </th>
            {products.map((p) => (
              <th
                key={p.id}
                className="font-data sticky top-0 z-20 bg-white px-2 py-2 text-center text-[10.5px] font-semibold tracking-[0.04em] uppercase"
                style={{
                  borderBottom: "1px solid oklch(0.91 0.008 240)",
                  minWidth: 90,
                }}
              >
                <div className="flex flex-col gap-0.5">
                  <span className="text-foreground line-clamp-2 print:line-clamp-none">
                    {p.name}
                  </span>
                  <span className="text-foreground/55 text-[9px] font-normal tracking-normal normal-case">
                    {p.vendor ?? "—"}
                  </span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {features.map((f) => {
            const domain = f.domain as Domain | null;
            return (
              <tr key={f.id} className="print:break-inside-avoid">
                <td
                  className="sticky left-0 z-10 bg-white px-3 py-2"
                  style={{
                    borderBottom: "1px solid oklch(0.96 0.005 240)",
                    borderLeft: domain ? `3px solid ${DOMAIN_COLOR[domain]}` : undefined,
                    minWidth: 280,
                    maxWidth: 360,
                  }}
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="line-clamp-2 text-[12.5px] leading-tight font-medium print:line-clamp-none">
                      {f.title}
                    </span>
                    <span className="text-foreground/55 text-[10px]">
                      {f.journey ? `${f.journey} · ` : ""}
                      {domain ? DOMAIN_LABEL[domain] : "—"}
                    </span>
                  </div>
                </td>
                {products.map((p) => {
                  const c = coverageMap.get(`${p.id}:${f.id}`) ?? null;
                  return (
                    <td
                      key={p.id}
                      className="px-1 py-1"
                      style={{ borderBottom: "1px solid oklch(0.96 0.005 240)" }}
                    >
                      <MatrixCell coverage={c} onPick={(next) => onPick(p, f, next)} />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ProductFilter({
  products,
  selectedIds,
  onChange,
}: {
  products: ProductRow[];
  selectedIds: Set<string>;
  onChange: (ids: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const filtered = useMemo(() => {
    if (query.trim().length === 0) return products;
    const q = query.toLowerCase();
    return products.filter(
      (p) => p.name.toLowerCase().includes(q) || (p.vendor ?? "").toLowerCase().includes(q)
    );
  }, [products, query]);

  const label =
    selectedIds.size === 0
      ? "All products"
      : selectedIds.size === 1
        ? (products.find((p) => selectedIds.has(p.id))?.name ?? "1 product")
        : `${selectedIds.size} products`;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-[oklch(0.91_0.008_240)] px-3 text-[12px] font-medium transition-colors hover:bg-[oklch(0.97_0.005_240)]"
        style={
          selectedIds.size > 0
            ? {
                borderColor: "oklch(0.55 0.17 190)",
                background: "oklch(0.95 0.05 190)",
                color: "oklch(0.35 0.17 190)",
              }
            : undefined
        }
      >
        <span className="max-w-[180px] truncate">{label}</span>
        {selectedIds.size > 0 ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onChange(new Set());
            }}
            className="hover:text-foreground inline-flex size-4 items-center justify-center rounded-sm"
          >
            <X className="size-3" />
          </span>
        ) : (
          <ChevronDown className="size-3" />
        )}
      </button>
      {open ? (
        <div className="absolute top-full left-0 z-40 mt-1 flex w-72 flex-col gap-2 rounded-md border border-[oklch(0.91_0.008_240)] bg-white p-2 shadow-lg">
          <input
            type="search"
            placeholder="Search products…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border-input h-8 rounded-md border bg-transparent px-2 text-[12px]"
          />
          <div className="flex items-center justify-between text-[11px]">
            <button
              type="button"
              onClick={() => onChange(new Set())}
              className="text-foreground/60 hover:text-foreground"
            >
              Clear
            </button>
            <span className="text-foreground/50">
              {selectedIds.size} of {products.length}
            </span>
          </div>
          <ul className="-mr-1 max-h-72 overflow-y-auto pr-1">
            {filtered.map((p) => {
              const checked = selectedIds.has(p.id);
              return (
                <li key={p.id}>
                  <label className="hover:bg-muted/40 flex cursor-pointer items-start gap-2 rounded px-1.5 py-1.5 text-[12px]">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(p.id)}
                      className="mt-0.5"
                    />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate font-medium">{p.name}</span>
                      {p.vendor ? (
                        <span className="text-foreground/55 truncate text-[10.5px]">
                          {p.vendor}
                        </span>
                      ) : null}
                    </div>
                  </label>
                </li>
              );
            })}
            {filtered.length === 0 ? (
              <li className="text-muted-foreground px-2 py-3 text-center text-[11px]">
                No products match.
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
